import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  Injector,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { Subscription, forkJoin, of, zip } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { bidJetRate, bidSafRate, formulaWeightToVolume, neatSafPriceOf, ratesNeeded, RateLimits, safFeeRate } from '../../../common/helpers/unit/bid-unit-rates';
import { Settings } from '../../../account/models';
import { SettingService } from '../../../account/services';
import { User } from '../../../auth/models';
import { UserSettings } from "../../../auth/models/user/user-settings";
import { averagePriceEnabled } from './average-price-gate';
import { DateTimeHelper } from '../../../common/helpers/datetime/datetime.helper';
import { CurrencyHelper } from "../../../common/helpers/currency/currency.helper";
import { FeesHelper } from "../../../common/helpers/fees/fees.helper";
import { Currency, CurrencyCollection } from '../../../common/models/currency';
import { PaymentMethodsCollection } from '../../../common/models/payment-method';
import { Units, UnitsCollection } from '../../../common/models/units';
import { CurrencyRatesService, UnitRatesService } from "../../../common/services";
import { FormulaAstNode, FormulaConfig, FormulaParserService, FormulaTooltipData } from '../../../common/services/formula-parser.service';
import { PlattsPricesService } from '../../../indexes/services';
import { indexRateKey } from '../../../indexes/services/platts/platts-prices.service';
import { LoadingIndicatorService, ToastService } from '../../../main/services';
import {
  Contract, ContractSupplier, ConversionFactor, CostOfCapital, Document,
  ROUND_SUPPLIER_STATUSES,
  Round,
  RoundSupplierDisplayOption, Tender,
} from '../../models';
import { AWARD_SELF_SUPPLY_OPTIONS, Offer } from '../../models/offer';
import { OFFER_VIEW_FIELDS, VA_ORDERED_FIELDS } from "../../models/offer/offer";
import { ROUND_STATUSES } from '../../models/round';
import { AWAITING_OFFER_STATUSES } from '../../models/round-supplier/round-supplier.statuses';
import {
  TenderAnalyticsSettingsService,
  TenderBidCopyToCurrentRoundService, TenderBidMockService, TenderBidsService,
  TenderContractBidsAnalysisService,
  TenderContractSuppliersService,
  TenderContractsService,
  TenderDocumentsService, TenderRevisionService, TenderService
} from '../../services';
import { TenderAwardsService } from '../../services/tender-awards.service';
import { TenderBidSubmitService } from "../../services/tender-bid-submit/tender-bid-submit.service";

@Component({
    selector: 'jft-tender-contract-bids-analysis',
    templateUrl: './tender-contract-bids-analysis.component.html',
    styleUrls: ['./tender-contract-bids-analysis.component.scss'],
    // changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class TenderContractBidsAnalysisComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
    public unitRates;
    public unitRatesByWeightToVolumeValue = {}; // unit-rate tables by weight-to-volume rate (USG per MT), other than the tender's
    private rateLimits: RateLimits | null = null;
    public currencyRates;
    public baseCurrency: Currency;
    public baseUnit: Units;
    public baseExchangeRateDate: any = null;
    public tender: Tender;
    public contract: Contract;
    public suppliers: ContractSupplier[] = [];
    public currentOffers: ContractSupplier[] = [];
    public mockOffers: ContractSupplier[] = [];
    public rounds: Round[] = [];
    public contractBidsDisplayOptions: RoundSupplierDisplayOption[];
    public offerStatuses = ROUND_SUPPLIER_STATUSES;
    public selfSupplyOptions = AWARD_SELF_SUPPLY_OPTIONS;
    public awaitingOfferStatuses = AWAITING_OFFER_STATUSES;
    public order_field: string = 'ranking';  // Default to ranking sort
    public order: string = 'ASC';  // Default to ascending (best to worst)
    public hideInvalidBids: boolean = false; // Default to show all bids (unchecked)
    public allSuppliers: ContractSupplier[] = []; // Store all suppliers before filtering
    public selectedSupplierForCancellation: ContractSupplier | null = null; // Store supplier for modal cancellation
    public allContracts: Contract[] = [];
    public currentContractIndex: number = -1;
    public isFullscreen: boolean = false;
    private static readonly FULLSCREEN_STORAGE_KEY = 'jft.bidAnalysisFullscreen';
    private analyticSetting: CostOfCapital;
    private formulaAst: FormulaAstNode | null = null;
    /** All active formulas (presets minus deleted + custom) */
    public activeFormulas: Record<string, FormulaConfig> = {};
    /** Human-readable labels for each active formula */
    public formulaLabels: Record<string, string> = {};

    // ViewChild references for modals
    @ViewChild('cancelAwardModal') cancelAwardModal!: TemplateRef<any>;
    @ViewChild('cancelTentativeAwardModal') cancelTentativeAwardModal!: TemplateRef<any>;
    @ViewChild('withdrawBidModal') withdrawBidModal!: TemplateRef<any>;

    // Define invalid bid statuses
    private readonly INVALID_BID_STATUSES = [
        ROUND_SUPPLIER_STATUSES.EXPIRED,
        ROUND_SUPPLIER_STATUSES.WITHDRAWN,
        ROUND_SUPPLIER_STATUSES.CANCELLED,
        ROUND_SUPPLIER_STATUSES.DECLINED,
        ROUND_SUPPLIER_STATUSES.DECLINED_SUBMITTED,
        ROUND_SUPPLIER_STATUSES.AWAITING,
        ROUND_SUPPLIER_STATUSES.SUBMISSION
    ];
    public params = {tenderId: null, contractId: null};
    public orderChangeSubscription: Subscription;
    public currentDate = new Date();
    public hiddenDisplayOptions: number;
    public isDataFullyLoaded: boolean = false;  // Track if all data is loaded and sorted
    public preCalculatedRowHeights: { [key: string]: number } = {};  // Store pre-calculated row heights
    public isLayoutCalculated: boolean = false;  // Track if layout has been pre-calculated
    public vAnalysisHeight: string = 'auto';  // Dynamic height for v_analysis container
    public round: Round;
    public roundStatuses = ROUND_STATUSES;
    public companySettings: Settings;
    public defaultDateFormat: string = DateTimeHelper.defaultDateLuxFormat();
    public tenderSettings: ConversionFactor;
    public userSettings: UserSettings;
    public usHiddenFields: OFFER_VIEW_FIELDS;
    public VIEW_FIELDS = OFFER_VIEW_FIELDS;
    public loadingAward: boolean = false;
    public currencies: CurrencyCollection;
    public units: UnitsCollection;
    public paymentMethods: PaymentMethodsCollection;
    public lastRound: Round;
    public roundNum: number;

  public dateRange: string = '12_month';
    public dateStartPriceBase: string = '';
    public dateEndPriceBase: string = '';
    public dateFormat = 'YYYY-MM-DD';
    public isBidFeedbacks: boolean = false;
    public mockBidName: string = '';
    public isAveragePriceEnabled: boolean = false;
    public safCentsMap = {
        'USC': 'USD',
        'ERC': 'EUR'
    }

    public usOrderedFields: OFFER_VIEW_FIELDS | any = VA_ORDERED_FIELDS;

    constructor(
        public user: User,
        private route: ActivatedRoute,
        private router: Router,
        protected toast: ToastService,
        private currencyRatesService: CurrencyRatesService,
        private unitRatesService: UnitRatesService,
        private service: TenderContractBidsAnalysisService,
        private supplierService: TenderContractSuppliersService,
        private tenderContractService: TenderContractsService,
        private tenderService: TenderService,
        private settingsService: SettingService,
        private loadingIndicatorService: LoadingIndicatorService,
        private plattsPricesService: PlattsPricesService,
        @Inject(TenderDocumentsService) protected documentService: TenderDocumentsService,
        private serviceCopyBid: TenderBidCopyToCurrentRoundService,
        private tenderBidsService: TenderBidsService,
        private injector: Injector,
        private ngbModal: NgbModal,
        private tenderRevisionService: TenderRevisionService,
        private serviceBidMock: TenderBidMockService,
        private serviceBidSubmit: TenderBidSubmitService,
        private cdr: ChangeDetectorRef,
        private awardsService: TenderAwardsService,
        private analyticsSettingsService: TenderAnalyticsSettingsService,
        private formulaParser: FormulaParserService,
    ) {
    }

    ngOnInit() {
        // Initialize loading state - hide content until all data is loaded
        this.isDataFullyLoaded = false;

        // Restore fullscreen state if the user was in fullscreen on the
        // previous location. Uses sessionStorage so the state persists
        // through prev/next location navigation but resets on tab close.
        if (typeof sessionStorage !== 'undefined' &&
            sessionStorage.getItem(TenderContractBidsAnalysisComponent.FULLSCREEN_STORAGE_KEY) === '1') {
            this.isFullscreen = true;
            document.body.classList.add('jft-analysis-fullscreen-lock');
        }

        // Calculate initial height for v_analysis container after DOM is ready
        setTimeout(() => {
            this.calculateVAnalysisHeight();
        }, 100);

        let data = this.route.snapshot.data,
            parentData = this.route.parent?.snapshot.data,
            parentParentData = this.route.parent?.parent?.snapshot.data,
            parentParentParentData = this.route.parent?.parent?.parent?.snapshot.data;

        // Find tender and rounds from any available route data in the hierarchy
        this.tender = data['tender'] || parentData?.['tender'] || parentParentData?.['tender'] || parentParentParentData?.['tender'];
        this.allSuppliers = data['suppliers'] || parentData?.['suppliers'] || parentParentData?.['suppliers'] || parentParentParentData?.['suppliers'] || [];
        this.suppliers = [...this.allSuppliers];
        this.contract = data['contract'] || parentData?.['contract'] || parentParentData?.['contract'] || parentParentParentData?.['contract'];
        this.rounds = data['rounds'] || parentData?.['rounds'] || parentParentData?.['rounds'] || parentParentParentData?.['rounds'] || [];

        // Fetch sibling contracts so the page can offer Previous/Next
        // location navigation without a full reload.
        if (this.tender?.id && this.contract?.id) {
            this.tenderContractService.fetch(this.tender.id).subscribe((contracts: any[]) => {
                this.allContracts = contracts || [];
                this.recomputeContractIndex();
            });
        }
        this.currencies = data['currencies'] || parentData?.['currencies'] || parentParentData?.['currencies'] || parentParentParentData?.['currencies'];
        this.units = data['units'] || parentData?.['units'] || parentParentData?.['units'] || parentParentParentData?.['units'];

        // Debug logging to see what data is available (can be removed in production)
        // console.log('Route data:', data);
        // console.log('Parent data (dashboard):', parentData);
        // console.log('Parent parent data (locations):', parentParentData);
        // console.log('Tender data:', parentParentParentData);

        // Initialize fallback check and logging
        if (!parentData) {
            console.error('Dashboard route data is missing');
            this.toast.error('Failed to load dashboard data. Please refresh the page.');
            return;
        }

        // Load hideInvalidBids preference from localStorage for this tender
        this.loadHideInvalidBidsPreference();
        this.applyInvalidBidsFilter();

        // Add null check before accessing contract
        if (!this.contract) {
            console.error('Contract data is missing');
            this.toast.error('Contract data could not be loaded. Please refresh the page.');
            return;
        }

        if (!this.tender) {
            console.error('Tender data is missing');
            this.toast.error('Tender data could not be loaded. Please refresh the page.');
            return;
        }

        this.roundNum = this.route.snapshot.params.rid;
        this.round = this.rounds.filter(item => item.round == this.roundNum)[0];

        this.paymentMethods = data['paymentMethods'];
        this.tenderSettings = data['tenderSettings']
        this.userSettings = data['userSettings']
        this.usHiddenFields = this.userSettings && this.userSettings['offer_view_field'] ? this.userSettings['offer_view_field'] : {} as OFFER_VIEW_FIELDS;

        this.lastRound = this.round;

        this.unitRates = data['unitRates'];
        this.baseCurrency = this.tenderSettings ? this.tenderSettings.currency : null;
        this.baseUnit = this.tenderSettings ? this.tenderSettings.units : null;

        if (this.userSettings && this.userSettings['offer_order_field']) {
            this.initOfferOrderField(this.userSettings['offer_order_field']);
        }
        this.service.resetOrder();
        this.initBaseExchangeRateDate();

        // The viewing user's own accepted S&P login (see average-price-gate.ts).
        if (averagePriceEnabled(this.userSettings)) {
            this.isAveragePriceEnabled = true;
        }

        // Load all active formulas (presets minus deleted + custom)
        // The User singleton is kept in sync by system-settings after save.
        const customFields = this.user?.company?.settings?.custom_fields;
        this.activeFormulas = FormulaParserService.getActiveFormulas(
            customFields?.formulas,
            customFields?.deleted_formulas,
        );
        this.formulaAst = this.activeFormulas['total_price']?.ast || this.formulaParser.getDefaultFormulaAst();
        // Pre-compute human-readable labels for tooltips
        this.formulaLabels = {};
        for (const [key, config] of Object.entries(this.activeFormulas)) {
            this.formulaLabels[key] = this.formulaParser.astToHumanReadable(config.ast);
        }

        // Kick off data load sequence so the loader stops even when there are only placeholders (no bids)
        this.getCurrencyRates();
    }

    /**
     * Apply default ranking sorting for supplier bids from best to worst
     * Only sorts supplier bids, not current contracts or mock offers
     * This is called after data is loaded and ranking is calculated
     */
    private applyDefaultRankingSortingIfNeeded(offers) {
        // Apply default ranking sorting if it's the current sort field
        // This ensures the default sorting is applied on initial page load
        // Fix: Only apply to main suppliers array to prevent race conditions
        if (this.order_field === 'ranking' && offers === this.suppliers) {
            this.suppliers = this.applySort(offers, 'ranking');
        }
    }

    /**
     * Pre-calculate layout dimensions by allowing content to render hidden, then measuring
     * This prevents visual jumps when the actual content is shown
     */
    private preCalculateLayoutDimensions(callback: () => void) {
        // Allow content to render hidden, then measure heights
        setTimeout(() => {
            this.measureAndApplyRowHeights();
            this.isLayoutCalculated = true;
            callback();
        }, 100); // Normal timeout for production
    }

    /**
     * Reset all row heights to auto to allow natural height calculation
     */
    private resetRowHeights() {
        const container = document.querySelector('.va-table');
        if (!container) {
            return;
        }

        // Find all elements with row-index classes and reset their heights
        const allElements = container.querySelectorAll('[class*="row-index-"]');
        allElements.forEach((element: HTMLElement) => {
            element.style.removeProperty('height');
            element.style.removeProperty('min-height');
            element.style.removeProperty('max-height');
            element.removeAttribute('data-equal-height-applied');
        });
    }

    /**
     * Force layout recalculation by resetting flags and recalculating heights
     */
    public forceLayoutRecalculation() {
        this.isLayoutCalculated = false;
        this.equalHeightApplied = false;
        this.preCalculatedRowHeights = {};

        // Reset existing heights first
        this.resetRowHeights();

        // Wait for Angular change detection and DOM updates
        this.cdr.detectChanges();

        setTimeout(() => {
            this.measureAndApplyRowHeights();
            this.isLayoutCalculated = true;
            this.equalHeightApplied = true;
        }, 300); // Increased timeout to ensure DOM is fully updated
    }



    /**
     * Measure and apply row heights from the actual hidden content
     */
    private measureAndApplyRowHeights() {
        const container = document.querySelector('.va-table');
        if (!container) {
            return;
        }

        // Find all unique row indices dynamically
        const allElements = container.querySelectorAll('[class*="row-index-"]');
        const rowIndices = new Set<number>();

        allElements.forEach(element => {
            const classes = element.className.split(' ');
            classes.forEach(cls => {
                if (cls.startsWith('row-index-')) {
                    const index = parseInt(cls.replace('row-index-', ''));
                    if (!isNaN(index)) {
                        rowIndices.add(index);
                    }
                }
            });
        });

        // Process each row index found
        Array.from(rowIndices).forEach(i => {
            const elements = container.getElementsByClassName(`row-index-${i}`);
            if (elements.length > 0) {
                let maxHeight = 0;
                Array.from(elements).forEach((element: HTMLElement) => {
                    const height = element.getBoundingClientRect().height;
                    if (height > maxHeight) {
                        maxHeight = height;
                    }
                });

                // Apply the max height to all elements in this row with !important to prevent overrides
                if (maxHeight > 0) {
                    Array.from(elements).forEach((element: HTMLElement) => {
                        element.style.setProperty('height', `${maxHeight}px`, 'important');
                        element.style.setProperty('min-height', `${maxHeight}px`, 'important');
                        element.style.setProperty('max-height', `${maxHeight}px`, 'important');
                        // Add a data attribute to track that equal height has been applied
                        element.setAttribute('data-equal-height-applied', 'true');
                    });
                }
            }
        });
    }

    /**
     * Calculate the exact height for v_analysis container to eliminate page scrolling
     * The v_analysis container should take up all remaining screen space
     */
    private calculateVAnalysisHeight() {
        // Get viewport height
        const viewportHeight = window.innerHeight;

        // Calculate heights of all elements above v_analysis
        let usedHeight = 0;

        // 1. Navbar height
        const navbar = document.querySelector('.navbar');
        if (navbar) {
            usedHeight += navbar.getBoundingClientRect().height;
        } else {
            usedHeight += 56; // Default Bootstrap navbar height
        }

        // 2. Account for v_analysis margin-top (mt-4 = 1.5rem = 24px)
        usedHeight += 24;

        // 3. Account for any body margins/padding
        const body = document.body;
        const bodyStyles = window.getComputedStyle(body);
        usedHeight += parseInt(bodyStyles.marginTop) || 0;
        usedHeight += parseInt(bodyStyles.paddingTop) || 0;

        // 4. Account for any container margins/padding above v_analysis
        const container = document.querySelector('.container, .container-fluid');
        if (container) {
            const containerStyles = window.getComputedStyle(container);
            usedHeight += parseInt(containerStyles.paddingTop) || 0;
        }

        // Calculate available height for v_analysis container
        const availableHeight = viewportHeight - usedHeight;

        // Subtract buffer to eliminate page scroll completely while maximizing space
        const finalAvailableHeight = availableHeight - 150; // Buffer to prevent page scroll and account for margins

        // Set minimum height to prevent too small containers
        const minHeight = 300;
        const finalHeight = Math.max(finalAvailableHeight, minHeight);

        this.vAnalysisHeight = `${finalHeight}px`;
    }

    /**
     * Handle window resize to recalculate v_analysis height
     */
    @HostListener('window:resize', ['$event'])
    onWindowResize(event: any) {
        this.calculateVAnalysisHeight();
    }

    private setUserDefaultDateFormat() {
        this.settingsService.fetch().subscribe((settings) => {
            this.companySettings = settings;
            if (this.companySettings.date_format) {
                this.defaultDateFormat = DateTimeHelper.dateFormatToLuxFormat(this.companySettings.date_format);
            }
        });
    }

    private addIsBidFeedbacksToBid() {
        this.suppliers.forEach(supplier => {
            if (supplier.bid && supplier.bid.feedback) {
                this.isBidFeedbacks = true;
            }
        })
    }

    private initOrderChangeSubscription() {
        this.orderChangeSubscription = this.service.onOrderChange.subscribe((suppliers) => {
            this.allSuppliers = suppliers;
            this.suppliers = [...this.allSuppliers];
            this.applyInvalidBidsFilter();
            this.initRoundDateRange();

            // Pre-calculate layout dimensions before showing content
            this.preCalculateLayoutDimensions(() => {
                // Hide loading indicator and show content
                this.loadingIndicatorService.hide();
                this.isDataFullyLoaded = true;

                // Trigger equal height calculation after content is shown
                setTimeout(() => {
                    this.measureAndApplyRowHeights();
                }, 100);
            });
        });
    }

    private initBaseExchangeRateDate() {
        if (this.tenderSettings.exchangeRateDate && this.tenderSettings.isExchangeRateFixed) {
            this.baseExchangeRateDate = this.tenderSettings.exchangeRateDate.format(this.dateFormat);
        }
    }

    private getCurrentContractsAndMockBids(callback?: () => void) {
        let obsCurrContract = this.getObsCurrContract();
        let obsMockBid = this.getObsMockBid();

        forkJoin([obsCurrContract, obsMockBid]).subscribe(results => {
            let resCurrContract = results[0];
            // @ts-ignore
            let resMockBid = results[1];
            // @ts-ignore
            this.currentOffers = resCurrContract;
            this.updateSecondPartOfBidsData(this.currentOffers);
            // @ts-ignore
            this.mockOffers = resMockBid;
            this.updateSecondPartOfBidsData(this.mockOffers);

            // Recalculate ranking across suppliers + mock bids combined
            this.recalculateCombinedRanking();

            this.updateAveragePriceBases();

            // Execute callback after all data is loaded and processed
            if (callback) {
                callback();
            }
        });
    }

    private getObsMockBid() {
        return this.serviceBidMock.fetch(this.tender.id, this.contract.id);
    }

    private getObsCurrContract() {
        return this.tenderRevisionService.fetchTenderLocationCurrentContracts(this.tender.id, this.contract.id);
    }

    private initRoundDateRange() {
        if (this.round && this.round.dateRange && this.round.dateRange.id) {
            this.dateRange = this.round.dateRange.code;

            if (this.round.dateRangeStart && this.round.dateRangeEnd) {
                this.dateStartPriceBase = moment(this.round.dateRangeStart).format(this.dateFormat);
                this.dateEndPriceBase = moment(this.round.dateRangeEnd).format(this.dateFormat);
            }
        } else {
            //this.saveDateRange();
        }
        this.updateAveragePriceBases();
    }

    public onDateRangePriceBase(event) {
        this.dateRange = event.code;
        this.dateStartPriceBase = event.dateStart;
        this.dateEndPriceBase = event.dateEnd;

        this.updateAveragePriceBases();
        // Only users who can manage the tender store the period (it drives everyone's
        // totals); a Viewer's pick is averaged here and not stored.
        if (this.user.canManage()) {
            this.saveDateRange();
        }
    }

    public saveDateRange() {
        let dateS = this.dateStartPriceBase ? moment(this.dateStartPriceBase).format(this.dateFormat) : '';
        let dateE = this.dateEndPriceBase ? moment(this.dateEndPriceBase).format(this.dateFormat) : '';

        this.service.saveDateRange(this.tender.id, this.contract.id, this.dateRange, dateS, dateE)
            .subscribe(() => {
            }, (e) => {
                this.toast.error(e);
            });
    }

    private updateAveragePriceBases() {
        this.getAverageIndexPrices(this.suppliers);
        this.getAverageIndexPrices(this.currentOffers);
        this.getAverageIndexPrices(this.mockOffers);
    }

    public sort($event: Event, field: string) {
        $event.preventDefault();
        $event.stopPropagation();

        // Show loading state and hide content during sorting
        this.isDataFullyLoaded = false;
        this.isLayoutCalculated = false;
        this.loadingIndicatorService.show();

        if (this.order_field == field) {
            this.order = this.order == 'ASC' ? 'DESC' : 'ASC';
        } else {
            this.order = 'DESC';
        }
        this.order_field = field;

        if (field == 'total' || field == 'diff_fees' || field == 'differential' || field == 'ranking' || field == 'ranking_gap') {
            // For client-side calculated fields, apply sorting directly without fetching new data
            this.applySortingAndShowContent(field);
        } else if (this.round) {
            // For server-side fields, fetch sorted data from server
            this.service.setOrder(this.order_field, this.order, this.tender, this.contract, this.round.round);
        }
    }

    /**
     * Apply sorting to client-side calculated fields and show content
     * This prevents race conditions by not fetching fresh data from server
     */
    private applySortingAndShowContent(field: string) {
        // Apply sorting based on field
        let sortField: string;
        switch (field) {
            case 'total':
                sortField = 'total_field';
                break;
            case 'diff_fees':
                sortField = 'diff_plus_vbfees';
                break;
            case 'differential':
                sortField = 'exchanged_differential';
                break;
            case 'ranking':
                sortField = 'ranking';
                break;
            case 'ranking_gap':
                sortField = 'ranking_gap';
                break;
            default:
                sortField = field;
        }

        // Apply sorting to suppliers array
        this.suppliers = this.applySort(this.suppliers, sortField);

        // Apply sorting to mock offers array
        this.mockOffers = this.applySortSimple(this.mockOffers, sortField);

        // Update display order for CSS interleaving
        this.updateDisplayOrder();

        // Pre-calculate layout dimensions before showing content
        this.preCalculateLayoutDimensions(() => {
            // Hide loading indicator and show content
            this.loadingIndicatorService.hide();
            this.isDataFullyLoaded = true;

            // Trigger equal height calculation after content is shown
            setTimeout(() => {
                this.measureAndApplyRowHeights();
            }, 100);
        });
    }

    public refreshContractBids(callback?: () => void) {
        if (!this.round) {
            if (callback) callback();
            return;
        }
        this.service.fetch(this.tender.id, this.contract.id, this.round.round).subscribe((data) => {
            this.allSuppliers = data;
            this.suppliers = [...this.allSuppliers];
            this.applyInvalidBidsFilter();
            this.getDisplayOptions();
            this.initRoundDateRange(); //todo refactor
            this.updateSecondPartOfBidsData(this.suppliers);

            // Recalculate combined ranking if mock offers are already loaded
            if (this.mockOffers && this.mockOffers.length) {
                this.recalculateCombinedRanking();
            }

            this.loadingIndicatorService.hide();

            // Agreed weight-to-volume rates (decisions #46, #47): load a table for every rate a
            // bid needs (its own Jet A-1, its SAF, the blend for the SAF fee) other than the
            // tender's, which the route resolver already loaded. Keyed by the rate asked for.
            this.unitRatesService.fetchConversion().pipe(
                catchError(() => of(null)),
                switchMap((limits: RateLimits | null) => {
                    this.rateLimits = limits;
                    const rates = limits
                        ? ratesNeeded(this.suppliers.map(offer => offer?.bid), this.tenderUsgPerMt(), limits)
                            .filter(rate => !this.unitRatesByWeightToVolumeValue[rate])
                        : [];
                    if (!rates.length) {
                        return of([]);
                    }
                    return forkJoin(rates.map(rate => this.unitRatesService.fetchAll({ w_to_volume: rate }).pipe(
                        map(table => ({ rate, table })), catchError(() => of(null)))));
                }),
            ).subscribe((results: any[]) => {
                (results || []).filter(Boolean).forEach(({ rate, table }) => this.unitRatesByWeightToVolumeValue[rate] = table);
                this.getRefreshContractBidsData(callback);
            });
        }, (e) => {
            this.toast.error(e);
        });
    }

    private getRefreshContractBidsData(callback: () => void) { //todo refactor
        this.applyInvalidBidsFilter();
        this.getDisplayOptions();
        this.initRoundDateRange(); //todo refactor
        this.updateSecondPartOfBidsData(this.suppliers);
        this.loadingIndicatorService.hide();

        // Reset layout calculation flags to force recalculation
        this.isLayoutCalculated = false;
        this.equalHeightApplied = false;
        this.preCalculatedRowHeights = {};

        // Reset existing heights first
        this.resetRowHeights();

        // Force Angular change detection
        this.cdr.detectChanges();

        // Recalculate layout after data update with longer timeout
        setTimeout(() => {
            this.measureAndApplyRowHeights();
            this.isLayoutCalculated = true;
            this.equalHeightApplied = true;

            // Execute callback after layout is recalculated
            if (callback) {
                callback();
            }
        }, 500); // Increased timeout to ensure DOM is fully updated
    }

    public saveFieldIncludeTotalFees(fee, offer){
        this.service.updateFeeIsAddInTotal(this.tender.id, this.contract.id, offer.id, fee.id,
            {'is_add_in_total': fee['is_add_in_total']})
            .subscribe((data) => {
        }, (e) => {
            this.toast.error(e);
        });
    }

    public saveFieldIncludeTotalPerFees(fee, offer){
        this.service.updatePerFeeIsAddInTotal(this.tender.id, this.contract.id, offer.id, fee.id,
            {'is_add_in_total': fee['is_add_in_total']})
            .subscribe((data) => {
        }, (e) => {
            this.toast.error(e);
        });
    }

    public onIncludeTotalFees(event, fee, offer){
        this.saveFieldIncludeTotalFees(fee, offer);
        this.updateSecondPartOfBidsData(this.suppliers);
        this.recalculateCombinedRanking();
    }

    public onIncludeTotalFeesMockOffers(event, fee, offer){
        this.saveFieldIncludeTotalFees(fee, offer);
        this.updateSecondPartOfBidsData(this.mockOffers);
        this.recalculateCombinedRanking();
    }

    public onIncludeTotalFeesCurrentOffers(event, fee, offer){
        this.saveFieldIncludeTotalFees(fee, offer);
        this.updateSecondPartOfBidsData(this.currentOffers);
    }

    public onIncludeTotalPerFees(event, fee, offer){
        this.saveFieldIncludeTotalPerFees(fee, offer);
        this.updateSecondPartOfBidsData(this.suppliers);
        this.recalculateCombinedRanking();
    }

    public onIncludeTotalPerFeesMockOffers(event, fee, offer){
        this.saveFieldIncludeTotalPerFees(fee, offer);
        this.updateSecondPartOfBidsData(this.mockOffers);
        this.recalculateCombinedRanking();
    }

    public onIncludeTotalPerFeesCurrentOffers(event, fee, offer){
        this.saveFieldIncludeTotalPerFees(fee, offer);
        this.updateSecondPartOfBidsData(this.currentOffers);
    }

    public isSort(field) {
        if (this.order_field === field) {
            return this.order === 'DESC';
        }
        return false;
    }

    public isActiveSort(field) {
        return this.order_field === field;
    }

    public countHiddenDisplayOptions() {
        this.hiddenDisplayOptions = 0;

        this.contractBidsDisplayOptions.forEach(value => {
            if (value.display_option == false) {
                this.hiddenDisplayOptions++;
            }
        });
    }

    public getDisplayOptions() {
        this.service.getDisplayOptions(this.tender.id, this.contract.id).subscribe((data) => {
            this.contractBidsDisplayOptions = data;
            this.countHiddenDisplayOptions();
            this.applyInvalidBidsFilter();
        }, (e) => {
            this.toast.error(e);
        });
    }

    public hideColumn(id) {
        let data = RoundSupplierDisplayOption.create({id: id, display_option: false});

        this.service.applyDisplayOptions(this.tender.id, this.contract.id, [data]).subscribe(() => {
            this.refreshContractBids();
            this.getMockOffers();
        }, (e) => {
            this.toast.error(e);
        });
    }

    public isAirPortDeliveryMethods() {
        return !!(this.suppliers.filter(item => item.bid ? item.bid.airPortDeliveryMethods.length : 0)).length;
    }

    public isAirCraftDeliveryMethods() {
        return !!(this.suppliers.filter(item => item.bid ? item.bid.airCraftDeliveryMethod.name : 0)).length;
    }

    public award(offer: ContractSupplier) { //todo refactor
        this.loadingAward = true;

        // Show loading overlay and hide content during award process
        this.isDataFullyLoaded = false;
        this.loadingIndicatorService.show();

        // Directly submit the tentative award without modal
        this.submitAward(offer);
    }

    /**
     * Award dates of an offer. Offers imported as current contracts belong to a past tender, so
     * when the bid carries no explicit award dates we fall back to the contract the offer was made
     * against rather than the contract currently on screen.
     */
    public awardContractDate(offer: ContractSupplier, boundary: 'start' | 'end'): Date {
        const bidDate = boundary === 'start' ? offer?.bid?.date_contract_start : offer?.bid?.date_contract_end;
        if (bidDate) {
            return bidDate.toDate();
        }
        const contract = offer?.round?.contract || this.contract;
        const contractDate = boundary === 'start' ? contract?.dateStart : contract?.dateEnd;
        return contractDate ? contractDate.toJSDate() : null;
    }

    public editCurrentContractAwardedConditions(data) {
        this.supplierService.editCurrentContractAwardedConditions(this.tender, this.contract, data).subscribe(() => {
            this.getCurrentContractsAndMockBids();
            this.refreshTenderAndContract(`Location: ${this.contract.location.getLabel()} Contract Conditions have been updated`);
        }, (e) => {
            this.isDataFullyLoaded = true;
            this.loadingIndicatorService.hide();
            this.toast.error(e);
        });
    }

    public cancelAward(offer: any) { //todo refactor
        this.loadingAward = true;

        // Show loading overlay and hide content during cancel award process
        this.isDataFullyLoaded = false;
        this.loadingIndicatorService.show();

        this.supplierService.cancelAward(this.tender, this.contract, offer).subscribe(() => {
            this.refreshContractBids();
            this.refreshTenderAndContract(`Location: ${this.contract.location.getLabel()} has been canceled awarded status`);
        }, (e) => {
            this.loadingAward = false;
            offer.awardedPercent = null;
            this.isDataFullyLoaded = true; // Restore content visibility on error
            this.loadingIndicatorService.hide();
            this.toast.error(e);
        });
    }

    public cancelTentativeAward(offer: any) {
        this.loadingAward = true;

        // Show loading overlay and hide content during cancel tentative award process
        this.isDataFullyLoaded = false;
        this.loadingIndicatorService.show();

        this.awardsService.cancelTentativeAward(this.tender.id, offer.id).subscribe(() => {
            this.refreshContractBids();
            this.refreshTenderAndContract(`Tentative award for location: ${this.contract.location.getLabel()} has been cancelled`);
        }, (e) => {
            this.loadingAward = false;
            this.isDataFullyLoaded = true; // Restore content visibility on error
            this.loadingIndicatorService.hide();
            this.toast.error(e);
        });
    }

    public openCancelAwardModal(supplier: ContractSupplier) {
        this.selectedSupplierForCancellation = supplier;
        this.ngbModal.open(this.cancelAwardModal, { centered: true, size: 'lg' });
    }

    public openCancelTentativeAwardModal(supplier: ContractSupplier) {
        this.selectedSupplierForCancellation = supplier;
        this.ngbModal.open(this.cancelTentativeAwardModal, { centered: true, size: 'lg' });
    }

    public confirmCancelAward(modal: any) {
        if (this.selectedSupplierForCancellation) {
            this.cancelAward(this.selectedSupplierForCancellation);
            modal.close();
        }
    }

    public confirmCancelTentativeAward(modal: any) {
        if (this.selectedSupplierForCancellation) {
            this.cancelTentativeAward(this.selectedSupplierForCancellation);
            modal.close();
        }
    }

    public openWithdrawBidModal(supplier: ContractSupplier) {
        this.selectedSupplierForCancellation = supplier;
        this.ngbModal.open(this.withdrawBidModal, { centered: true });
    }

    public confirmWithdrawBid(modal: any) {
        if (this.selectedSupplierForCancellation) {
            this.withdrawBid(this.selectedSupplierForCancellation);
            modal.close();
        }
    }

    private submitAward(offer: ContractSupplier) {
        this.supplierService.award(this.tender, this.contract, offer).subscribe(() => {
            this.refreshContractBids();
            this.refreshTenderAndContract(`Location: ${this.contract.location.getLabel()} awarded`);
        }, (e) => {
            this.loadingAward = false;
            offer.awardedPercent = null;
            this.isDataFullyLoaded = true; // Restore content visibility on error
            this.loadingIndicatorService.hide();
            this.toast.error(e);
        });
    }

    private countSumAwardedPercent() {
        let sum = 0;
        this.suppliers.forEach(offer => {
            if (offer.awardedPercent) {
                sum += parseInt(offer.awardedPercent, 10);
            }
        })
        return sum;
    }

    public withdrawBid(offer: ContractSupplier) {
        this.loadingAward = true;

        // Show loading overlay and hide content during withdraw process
        this.isDataFullyLoaded = false;
        this.loadingIndicatorService.show();

        this.supplierService.withdrawBid(this.tender, this.contract, offer).subscribe(() => {
            this.refreshContractBids();
            this.refreshTenderAndContract(`Bid withdrawn for location: ${this.contract.location.getLabel()}`);
        }, (e) => {
            this.loadingAward = false;
            this.isDataFullyLoaded = true;
            this.loadingIndicatorService.hide();
            this.toast.error(e);
        });
    }



    private refreshTenderAndContract(message: string) {
        zip(
            this.tenderContractService.renew(this.tender, this.contract),
            this.tenderService.renew(this.tender)
        ).subscribe((data) => {
            this.contract = data[0];
            this.tender = data[1];
            this.round = this.contract.currentRound;
            this.loadingAward = false;
            this.loadingIndicatorService.hide();
            this.toast.success(message);

            // Refresh the supplier data after contract/tender updates to ensure consistency
            this.refreshContractBids(() => {
                // Restore content visibility after all data is refreshed and layout is recalculated
                this.isDataFullyLoaded = true;
            });
        });
    }

    public isSubmittedBid() {
        return !!(this.suppliers.filter(item => item.bid ? item.bid : 0)).length;
    }

    public ngAfterViewInit() {
        // Equal height will be handled by ngAfterViewChecked when data is loaded
    }

    private equalHeightApplied = false;

    public ngAfterViewChecked() {
        // Only apply equal height once after data is loaded and view is stable
        if (this.isDataFullyLoaded && !this.equalHeightApplied) {
            setTimeout(() => {
                this.measureAndApplyRowHeights();
                this.equalHeightApplied = true;
            }, 100);
        }
    }

    public ngOnDestroy() {
        if (this.orderChangeSubscription && typeof this.orderChangeSubscription.unsubscribe === 'function') {
            this.orderChangeSubscription.unsubscribe();
        }
        // Always drop the body lock on teardown. The sessionStorage flag
        // (if set) will re-add it when the next analysis page mounts.
        document.body.classList.remove('jft-analysis-fullscreen-lock');
    }

    public roundChanged(round) {
        this.loadingIndicatorService.show();
        this.round = round;
        this.suppliers = [];

        if (this.round) {
            this.router.navigate(['analysis', 'round', this.round.round], { relativeTo: this.route.parent });
        }
    }

    public isCurrentRound() {
        return this.round && this.contract && this.contract.currentRound && this.round.round == this.contract.currentRound.round;
    }

    public downloadDocument(doc: Document) {
        return this.documentService.fetchFile(this.tender.id, doc);
    }

    private getAverageIndexPrices(offers) {
        if (this.isAveragePriceEnabled == false){
            return
        }
        let params = {
            'currencyId': this.tenderSettings.currency.id,
            'unitId': this.tenderSettings.units.id,
            'dateRange': this.dateRange,
            'startDate': this.dateStartPriceBase,
            'endDate': this.dateEndPriceBase,
            'tenderId': this.tender.id,
        };

        // Deduplicate API calls: group offers by unique (indexId, bateCode) key
        const uniqueKeys: Map<string, { indexId: any, bateCode: any, wToVolume: string }> = new Map();
        const offerKeyMap: { offer: any, key: string }[] = [];

        offers.forEach((offer) => {
            if (offer.bid && offer.bid.index && (offer.bid.index.index_code.provider == 'platts' || offer.bid.index.index_code.provider == 'argus' || offer.bid.index.index_code.provider == 'opis')) {
                const indexId = offer.bid.index.index_code.id;
                const bateCode = offer.bid.index.bate ? offer.bid.index.bate.id : '';
                // Per bid rate too: an MT-quoted index averages to a different price per weight to volume.
                const wToVolume = indexRateKey(offer.bid);
                const key = `${indexId}_${bateCode}_${wToVolume}`;
                uniqueKeys.set(key, { indexId, bateCode, wToVolume });
                offerKeyMap.push({ offer, key });
            }
        });

        if (uniqueKeys.size === 0) return;

        // Make one API call per unique index+bate combination
        const keys = Array.from(uniqueKeys.keys());
        const uniqueObs = keys.map(key => {
            const { indexId, bateCode, wToVolume } = uniqueKeys.get(key);
            return this.plattsPricesService.fetchPriceAverage(indexId, bateCode, this.dateRange, { ...params, wToVolume });
        });

        forkJoin(uniqueObs).subscribe((data) => {
            // Map results back by key
            const resultsByKey: { [key: string]: any } = {};
            keys.forEach((key, i) => resultsByKey[key] = data[i]);

            offerKeyMap.forEach(({ offer, key }) => {
                const val = resultsByKey[key];
                offer.bid.index['average_price'] = (typeof val === 'number' && !isNaN(val)) ? val : 0;
            });
            this.updateSecondPartOfBidsData(offers);
            this.recalculateCombinedRanking();
            this.cdr.detectChanges();
        });
    }

    public getObsAverageIndexPrice(offer, params) {
        return this.plattsPricesService.fetchPriceAverage(offer.bid.index.index_code.id,
            offer.bid.index.bate ? offer.bid.index.bate.id : '', this.dateRange, { ...params, wToVolume: indexRateKey(offer.bid) });
    }



    public changeRound(round: Round) {
        this.roundChanged(round);
    }

    public moveToCurrentRound(offer: ContractSupplier) {
        this.serviceCopyBid.copy(this.tender, this.contract, offer.id).subscribe(item => {
            this.toast.success('Offer has been copied to current round');
            this.changeRound(this.lastRound);
        }, error => {
            this.toast.error(error);
        })
    }

    public moveDeclinedToCurrentRound(offer: ContractSupplier) {
        this.serviceCopyBid.copyDeclined(this.tender, this.contract, offer.id).subscribe(item => {
            this.toast.success('Supplier has been invited to the current round');
            this.changeRound(this.lastRound);
        }, error => {
            this.toast.error(error);
        })
    }

    public getCurrencyRates() {
        this.currencyRatesService.fetch(this.baseCurrency.code, {
            tender: this.tender.id,
            date: this.baseExchangeRateDate
        }, false).subscribe((currencies: []) => {
            this.currencyRates = currencies;

            ////Error: Cannot read properties of undefined (reading 'NOK') fix todo refactor
            this.initOrderChangeSubscription();
            this.addIsBidFeedbacksToBid();
            this.setUserDefaultDateFormat();

            // Fix: Load contract bids first, then load additional data
            this.loadAllDataSequentially();
            ////Error: Cannot
        })
    }

    private loadAllDataSequentially() {
        // Set loading state to hide content during data loading
        this.isDataFullyLoaded = false;
        this.isLayoutCalculated = false;

        // First load the main supplier bid data
        this.refreshContractBids(() => { //also get custom unit rates here
            // Then load current contracts and mock bids after main data is loaded
            this.getCurrentContractsAndMockBids(() => {
                // All data loaded, now pre-calculate layout before showing content
                this.preCalculateLayoutDimensions(() => {
                    // Layout calculated, now show content
                    this.isDataFullyLoaded = true;

                    // Trigger equal height calculation after content is shown
                    setTimeout(() => {
                        this.measureAndApplyRowHeights();
                    }, 200);
                });
            });
        });
    }

    /** Whether the supplier's input differs from the base currency/unit it is displayed in. */
    public isConverted(currencyCode: string, unitCode: string = null) {
        return CurrencyHelper.isConverted(
            currencyCode, this.baseCurrency?.code, unitCode, this.baseUnit?.code
        );
    }

    public toCurrencyToUnit(value: number, currency: string, unit: string, currencyRates: {}, baseCurrencyCode: string, unitRates: {}, baseUnitCode: string) {
        let val = FeesHelper.convertToCurrency(currency, value, currencyRates, baseCurrencyCode);
        val = FeesHelper.convertToUnit(unit, val, unitRates, baseUnitCode);
        return val ? val.toFixed(6) : val;
    }

    private updateSecondPartOfBidsData(offers) {
        let totalPrices = [];
        offers.forEach(item => {
            if (item.bid) {
                const bidTables = this.unitRatesOfBid(item.bid);
                let unitRatesForBid = bidTables.jet;

                if (item.bid.volume_based_fees) {
                    item.bid['volume_fees_total'] = Number(this.calcTotalFees(item.bid, unitRatesForBid));
                    item.bid['volume_fees_all'] = FeesHelper.calcTotalFeesAll(item.bid, this.currencyRates, this.baseCurrency.code, unitRatesForBid, this.baseUnit.code);
                }

                if (item.bid.perUsageFees) {
                    item.bid['per_uplift_fees_total'] = Number(this.calcTotalPerFees(item.bid));
                    item.bid['per_uplift_fees_all'] = Number(FeesHelper.calcTotalPerFeesAll(item.bid, this.currencyRates, this.baseCurrency.code));
                }

                // per_uplift_fees_per_volume is now calculated via formula engine below

                let bid = item.bid;
                let waccValue: any = 0;
                let diffValue: any = 0;
                let marketValue: any = 0;
                let averagePrice: any = 0;

                if (bid['index'] && bid['index']['average_price'] !== undefined) {
                    averagePrice = bid['index']['average_price']
                }
                if (bid.differential && bid.differential.price && bid.differential.price.value && bid.differential.price.currency && bid.differential.units) {
                    diffValue = Number(this.toCurrencyToUnit(
                        bid.differential.price.value,
                        bid.differential.price.currency.code,
                        bid.differential.units.code,
                        this.currencyRates,
                        this.baseCurrency.code,
                        unitRatesForBid,
                        this.baseUnit.code
                    ));
                    item.bid['exchanged_differential'] = diffValue
                }

                item.bid['exchanged_wacc'] = waccValue

                if (bid.marketPrice && bid.marketPrice.price) {
                    marketValue = Number(this.toCurrencyToUnit(
                        bid.marketPrice.price['price']['value'],
                        bid.marketPrice.price['price'].currency.code,
                        bid.marketPrice.price['units'].code,
                        this.currencyRates,
                        this.baseCurrency.code,
                        unitRatesForBid,
                        this.baseUnit.code
                    ));
                    item.bid['exchanged_market_value'] = marketValue.toFixed(4)
                }

                let neatSafValue: any = 0;
                bid['exchanged_neat_saf'] = ''
                // The mandated price first, the legacy neat SAF as fallback (as bid_total._saf_rows).
                const neatSaf = neatSafPriceOf(bid);
                if (neatSaf) {
                    neatSafValue = this.toCurrencyToUnit(
                        neatSaf['price']['value'],
                        neatSaf['price']['currency']['code'],
                        neatSaf['units']['code'],
                        this.currencyRates,
                        this.baseCurrency.code,
                        bidTables.saf,
                        this.baseUnit.code
                    );
                    bid['exchanged_neat_saf'] = neatSafValue
                }

                // SAF cost and SAF premium are derived from the neat SAF price rather than
                // submitted directly. They are computed here, once, because the formulas used
                // to be inlined in each table column and drifted apart (the current/mock
                // columns had lost the /100 on the blend, inflating them 100x).
                bid['exchanged_saf_cost'] = null;
                bid['original_saf_cost'] = null;
                bid['exchanged_saf_premium'] = null;
                bid['original_saf_premium'] = null;
                bid['neat_saf_currency'] = null;
                bid['neat_saf_unit'] = null;

                const safBlend = bid['safMandateBlend'];
                if (neatSaf
                    && safBlend !== null && safBlend !== undefined) {
                    // safMandateBlend is a percentage (enum: 0.5, 1, 2, 3, 5), so it needs /100.
                    const blendFraction = Number(safBlend) / 100;
                    const neatSafPrice = Number(neatSaf['price']['value']);
                    const neatSafBasePrice = Number(bid['exchanged_neat_saf'] || 0);
                    const neatSafCurrency = neatSaf['price']['currency']['code'];
                    const neatSafUnit = neatSaf['units']['code'];

                    bid['neat_saf_currency'] = neatSafCurrency;
                    bid['neat_saf_unit'] = neatSafUnit;

                    // A per-unit price, so both currency and unit differ from the supplier's input.
                    bid['exchanged_saf_premium'] = blendFraction * neatSafBasePrice;
                    bid['original_saf_premium'] = blendFraction * neatSafPrice;

                    if (this.contract && this.contract.volume && this.contract.units) {
                        const volumeInBaseUnit = FeesHelper.convertToUnit(
                            this.contract.units.code, this.contract.volume, this.unitRates, this.baseUnit.code
                        );
                        // The original is the same cost with the currency step left out, rather
                        // than a separate derivation: that keeps the unit handling identical on
                        // both sides, so the two differ by exactly the currency the icon reports.
                        const neatSafPriceInBaseUnit = FeesHelper.convertToUnit(
                            neatSafUnit, neatSafPrice, bidTables.saf, this.baseUnit.code
                        );
                        bid['exchanged_saf_cost'] = volumeInBaseUnit * blendFraction * neatSafBasePrice;
                        bid['original_saf_cost'] = volumeInBaseUnit * blendFraction * neatSafPriceInBaseUnit;
                    }
                }

                let neatSafVoluntaryValue: any = 0;
                if (bid['neatSafVoluntary'] && bid['neatSafVoluntary']['price'] && bid['neatSafVoluntary']['price']['value']) {
                    neatSafVoluntaryValue = this.toCurrencyToUnit(
                        bid['neatSafVoluntary']['price']['value'],
                        bid['neatSafVoluntary']['price']['currency']['code'],
                        bid['neatSafVoluntary']['units']['code'],
                        this.currencyRates,
                        this.baseCurrency.code,
                        bidTables.saf,
                        this.baseUnit.code
                    );
                }

                let safFeeAmountUnits: any = 0;
                bid['saf_fee_amount_units'] = ''
                if (bid['safFeeAmountUnits'] && bid['safFeeAmountUnits']['price'] && bid['safFeeAmountUnits']['price']['value'] && bid['safFeeAmountUnits']['price']['currency'] && bid['safFeeAmountUnits']['units']) {
                    safFeeAmountUnits = this.toCurrencyToUnit(
                        bid['safFeeAmountUnits']['price']['value'],
                        bid['safFeeAmountUnits']['price']['currency']['code'],
                        bid['safFeeAmountUnits']['units']['code'],
                        this.currencyRates,
                        this.baseCurrency.code,
                        bidTables.safFee,
                        this.baseUnit.code
                    );
                    bid['exchanged_fee_amount'] = safFeeAmountUnits
                }

                // Build formula context with all converted field values
                let paymentTerms = bid.payment?.open_invoice?.payment_terms || 0;
                // Deduct transit time from payment terms (if set)
                if (bid.transitTime) {
                    paymentTerms = Math.max(0, paymentTerms - bid.transitTime);
                }
                let costOfCapital = this.tenderSettings?.costOfCapital || 0;

                // Compute WACC using pre-WACC subtotal (same fields as default formula minus wacc)
                let diffPlusVbfees = diffValue + (bid['volume_fees_total'] ? bid['volume_fees_total'] : 0);
                let preWaccTotal = parseFloat(diffPlusVbfees) + parseFloat(marketValue) + parseFloat(averagePrice) + parseFloat(safFeeAmountUnits);
                let waccComputed = 0;
                if (costOfCapital && paymentTerms) {
                    waccComputed = ((costOfCapital / 100) / 365) * paymentTerms * preWaccTotal * -1;
                }
                item.bid['exchanged_wacc'] = parseFloat(waccComputed.toFixed(4));

                // Compute volume per uplift for per-uplift formula
                let volumePerUplift = 0;
                if (this.contract.volume && this.contract.annualFrequency) {
                    volumePerUplift = Number(this.contract.volume) / Number(this.contract.annualFrequency);
                }
                let hock = FeesHelper.convertToUnit(this.contract.units.code, Number(this.contract.averageUplift), this.unitRates, this.baseUnit.code);

                const formulaContext: Record<string, number> = {
                    differential: parseFloat(diffValue) || 0,
                    volume_fees_total: bid['volume_fees_total'] || 0,
                    saf_fee_amount: parseFloat(safFeeAmountUnits) || 0,
                    per_uplift_fees_total: bid['per_uplift_fees_total'] || 0,
                    index_value: parseFloat(averagePrice) || 0,
                    market_price_value: parseFloat(marketValue) || 0,
                    weight_to_volume: formulaWeightToVolume(bid, this.tenderUsgPerMt(), this.rateLimits),
                    payment_terms: paymentTerms,
                    cost_of_capital: costOfCapital,
                    wacc: item.bid['exchanged_wacc'],
                    revised_differential: 0,
                    is_market_price: bid.marketPrice ? 1 : 0,
                    average_uplift: (hock && !isNaN(hock)) ? hock : 0,
                    volume_per_uplift: volumePerUplift,
                    contract_volume: Number(this.contract.volume) || 0,
                    annual_frequency: Number(this.contract.annualFrequency) || 0,
                    months_of_service: Number(this.contract.monthService) || 0,
                    monthly_volume: this.contract.monthService ? (Number(this.contract.volume) || 0) / Number(this.contract.monthService) : 0,
                    available_volume: Number(bid.fuelAvailabilityPercentage) || 0,
                    saf_mandated_price: parseFloat(neatSafValue) || 0,
                    saf_voluntary_price: parseFloat(neatSafVoluntaryValue) || 0,
                    saf_blend_percentage: Number(bid.safMandateBlend) || 0,
                    volume_fees_all: bid['volume_fees_all'] || 0,
                    per_uplift_fees_all: bid['per_uplift_fees_all'] || 0,
                };

                // Store formula context on bid for tooltip rendering
                bid['_formulaContext'] = formulaContext;

                // Evaluate all active formulas
                for (const [formulaKey, formulaConfig] of Object.entries(this.activeFormulas)) {
                    try {
                        bid[formulaKey] = parseFloat(this.formulaParser.evaluate(formulaConfig.ast, formulaContext).toFixed(4));
                    } catch (e) {
                        bid[formulaKey] = 0;
                    }
                }

                // Keep backward-compatible aliases for existing template references
                bid['total_field'] = bid['total_price'] || 0;
                bid['diff_plus_vbfees'] = bid['diff_vol_fees'] || 0;
                bid['diff_plus_vbfees_saf'] = bid['diff_saf_vol_fees'] || 0;
                bid['diff_plus_vbfees_saf_hock'] = bid['diff_saf_vol_event_fees'] || 0;
                bid['diff_plus_vbfees_hock'] = bid['diff_vol_event_fees'] || 0;

                // Blended price across the SAF and conventional-jet portions of the volume.
                // Depends on diff_plus_vbfees above, so it is computed after the aliases.
                bid['exchanged_conventional_jet_and_saf_premium'] = null;
                if (neatSaf
                    && bid['differential'] && bid['differential']['price']
                    && safBlend !== null && safBlend !== undefined) {
                    const blendFraction = Number(safBlend) / 100;
                    // exchanged_neat_saf, not the raw price: the other term is already in the
                    // base currency, so mixing in the supplier's own currency would produce a
                    // sum that belongs to neither.
                    const safPortion = blendFraction * Number(bid['exchanged_neat_saf'] || 0);
                    const jetPortion = (Number(averagePrice) + Number(bid['diff_plus_vbfees'] || 0))
                        * (1 - blendFraction);
                    bid['exchanged_conventional_jet_and_saf_premium'] = safPortion + jetPortion;
                }

                // Exclude withdrawn bids and current contracts from ranking
                if (item.status === ROUND_SUPPLIER_STATUSES.WITHDRAWN || offers === this.currentOffers) {
                    bid['ranking'] = null;
                    bid['ranking_gap'] = null;
                } else {
                    totalPrices.push({
                        bid_id: bid.id,
                        price: bid['total_field'],
                    })
                }
            }
        })

        this.setPriceRankingAngGap(totalPrices, offers);

        // Fix: Only apply sorting to main suppliers array, not during data processing
        // This prevents race conditions during page load
        if (offers === this.suppliers) {
            if (this.order_field == 'total') {
                this.suppliers = this.applySort(offers, 'total_field');
                this.mockOffers = this.applySortSimple(this.mockOffers, 'total_field');
            } else if (this.order_field == 'diff_fees') {
                this.suppliers = this.applySort(offers, 'diff_plus_vbfees');
                this.mockOffers = this.applySortSimple(this.mockOffers, 'diff_plus_vbfees');
            } else if (this.order_field == 'differential') {
                this.suppliers = this.applySort(offers, 'exchanged_differential');
                this.mockOffers = this.applySortSimple(this.mockOffers, 'exchanged_differential');
            } else if (this.order_field == 'ranking') {
                this.suppliers = this.applySort(offers, 'ranking');
                this.mockOffers = this.applySortSimple(this.mockOffers, 'ranking');
            } else if (this.order_field == 'ranking_gap') {
                this.suppliers = this.applySort(offers, 'ranking_gap');
                this.mockOffers = this.applySortSimple(this.mockOffers, 'ranking_gap');
            }

            // Apply default ranking sorting if no specific sorting is set
            this.applyDefaultRankingSortingIfNeeded(offers);

            // Update display order for CSS interleaving
            this.updateDisplayOrder();

            // Trigger equal height calculation after data is processed and sorted
            setTimeout(() => {
                this.measureAndApplyRowHeights();
            }, 100);
        }

        // Apply default ranking sorting if no specific sorting is set
        this.applyDefaultRankingSortingIfNeeded(offers);
    }

    /** The neat SAF price shown and priced: the mandated one, else the legacy one. */
    public neatSafOf(bid: any): any {
        return neatSafPriceOf(bid);
    }

    /** The tender's agreed rate: the resolver loaded the table at it (MT -> USG). */
    private tenderUsgPerMt(): number {
        return Number(this.unitRates?.['MT']?.['USG']) || this.rateLimits?.jet_a1?.default;
    }

    private tableAt(rate: number) {
        if (!rate || rate === this.tenderUsgPerMt()) {
            return this.unitRates;
        }
        return this.unitRatesByWeightToVolumeValue[rate] || this.unitRates;
    }

    /** Tables for one bid: Jet A-1 (differential, fees, market price), SAF (neat SAF prices),
     * and the SAF fee's blend. Without the limits loaded, every part uses the tender's table. */
    private unitRatesOfBid(bid) {
        if (!this.rateLimits) {
            return { jet: this.unitRates, saf: this.unitRates, safFee: this.unitRates };
        }
        const jet = bidJetRate(bid, this.tenderUsgPerMt(), this.rateLimits);
        const saf = bidSafRate(bid, this.rateLimits);
        return { jet: this.tableAt(jet), saf: this.tableAt(saf), safFee: this.tableAt(safFeeRate(bid, jet, saf)) };
    }

    private getCustomUnitRateIfExist(item) {
        return this.unitRatesOfBid(item.bid).jet;
    }

    private setPriceRankingAngGap(totalPrices: any[], offers) {
        if (totalPrices.length) {
            totalPrices.sort((a, b) => a.price - b.price);
            const bestPrice = totalPrices[0]['price'];
            totalPrices.forEach((item, index) => {
                item['ranking'] = index + 1
                item['ranking_gap'] = Math.abs(Number((bestPrice - item['price']).toFixed(4)))
            })
        }

        offers.forEach(item => {
            if (item.bid) {
                let obj = totalPrices.find(o => o.bid_id === item.bid.id);
                if (obj) {
                    item.bid['ranking'] = obj['ranking']
                    item.bid['ranking_gap'] = obj['ranking_gap']
                }
            }
        })
    }

    /**
     * Recalculate ranking across both supplier bids and mock bids combined.
     * This ensures mock bids are ranked alongside real supplier bids
     * rather than being ranked independently.
     */
    private recalculateCombinedRanking() {
        const allOffers = [...this.suppliers, ...this.mockOffers];
        const totalPrices = [];

        allOffers.forEach(item => {
            if (item.bid && item.bid['total_field'] !== undefined) {
                totalPrices.push({
                    bid_id: item.bid.id,
                    price: item.bid['total_field'],
                });
            }
        });

        this.setPriceRankingAngGap(totalPrices, allOffers);
        this.updateDisplayOrder();
    }

    /**
     * Compute display order for all offers (suppliers + mock) so that
     * CSS flex `order` can visually interleave mock bids among supplier bids.
     * Uses the current sort field to determine the interleaved position.
     */
    private updateDisplayOrder() {
        // Determine which bid field to sort by
        let sortField = 'ranking'; // default
        switch (this.order_field) {
            case 'total': sortField = 'total_field'; break;
            case 'diff_fees': sortField = 'diff_plus_vbfees'; break;
            case 'differential': sortField = 'exchanged_differential'; break;
            case 'ranking': sortField = 'ranking'; break;
            case 'ranking_gap': sortField = 'ranking_gap'; break;
        }

        // Merge all offers and sort them by the field
        const allOffers = [...this.suppliers, ...this.mockOffers];

        // Separate offers with and without valid sort values
        const withValue = allOffers.filter(item => item.bid && item.bid[sortField] != null && item.bid[sortField] !== 0);
        const withoutValue = allOffers.filter(item => !item.bid || item.bid[sortField] == null || item.bid[sortField] === 0);

        withValue.sort((a, b) => {
            return a.bid[sortField] - b.bid[sortField];
        });

        if (this.order === 'DESC') {
            withValue.reverse();
        }

        // Bids with values first, bids without values always at the end
        const sorted = [...withValue, ...withoutValue];

        // Assign _displayOrder (1-based to keep 0 for header/current contracts)
        sorted.forEach((item, index) => {
            item['_displayOrder'] = index + 1;
        });
    }

    private applySort(offers, field) {
        // Separate offers with and without bids
        const offersWithBids = offers.filter(item => item.bid);
        const offersWithoutBids = offers.filter(item => !item.bid);

        // Sort by status priority: Awarded, Tentatively Awarded, Received, Submission, Awaiting
        const awardedOffers = offersWithBids.filter(item => item.status === ROUND_SUPPLIER_STATUSES.AWARDED);
        const tentativelyAwardedOffers = offersWithBids.filter(item => item.status === ROUND_SUPPLIER_STATUSES.TENTATIVE_AWARDED);
        const receivedOffers = offersWithBids.filter(item => item.status === ROUND_SUPPLIER_STATUSES.RECEIVED);
        const submissionOffers = offersWithBids.filter(item => item.status === ROUND_SUPPLIER_STATUSES.SUBMISSION);
        const awaitingOffers = offersWithBids.filter(item => item.status === ROUND_SUPPLIER_STATUSES.AWAITING);
        const otherOffers = offersWithBids.filter(item =>
            ![ROUND_SUPPLIER_STATUSES.AWARDED, ROUND_SUPPLIER_STATUSES.TENTATIVE_AWARDED,
              ROUND_SUPPLIER_STATUSES.RECEIVED, ROUND_SUPPLIER_STATUSES.SUBMISSION,
              ROUND_SUPPLIER_STATUSES.AWAITING].includes(item.status)
        );

        // Also check for offers without bids (by status)
        const awardedWithoutBids = offersWithoutBids.filter(item => item.status === ROUND_SUPPLIER_STATUSES.AWARDED);
        const tentativelyAwardedWithoutBids = offersWithoutBids.filter(item => item.status === ROUND_SUPPLIER_STATUSES.TENTATIVE_AWARDED);
        const receivedWithoutBids = offersWithoutBids.filter(item => item.status === ROUND_SUPPLIER_STATUSES.RECEIVED);
        const submissionWithoutBids = offersWithoutBids.filter(item => item.status === ROUND_SUPPLIER_STATUSES.SUBMISSION);
        const awaitingWithoutBids = offersWithoutBids.filter(item => item.status === ROUND_SUPPLIER_STATUSES.AWAITING);
        const otherWithoutBids = offersWithoutBids.filter(item =>
            ![ROUND_SUPPLIER_STATUSES.AWARDED, ROUND_SUPPLIER_STATUSES.TENTATIVE_AWARDED,
              ROUND_SUPPLIER_STATUSES.RECEIVED, ROUND_SUPPLIER_STATUSES.SUBMISSION,
              ROUND_SUPPLIER_STATUSES.AWAITING].includes(item.status)
        );



        // Sort each group by the specified field
        const sortOffers = (offerArray) => {
            if (offerArray.length > 0) {
                offerArray.sort((a, b) => {
                    const aVal = a.bid[field];
                    const bVal = b.bid[field];
                    // Push null/undefined values to the end
                    if (aVal == null && bVal == null) return 0;
                    if (aVal == null) return 1;
                    if (bVal == null) return -1;

                    return this.order === 'ASC' ? aVal - bVal : bVal - aVal;
                });
            }
        };

        sortOffers(awardedOffers);
        sortOffers(tentativelyAwardedOffers);
        sortOffers(receivedOffers);
        sortOffers(submissionOffers);
        sortOffers(awaitingOffers);
        sortOffers(otherOffers);

        // Combine in order: Awarded, Tentatively Awarded, Received, Submission (Awaiting Submission), Awaiting (Awaiting Offer), Others
        const sortedOffers = awardedOffers
            .concat(awardedWithoutBids)
            .concat(tentativelyAwardedOffers)
            .concat(tentativelyAwardedWithoutBids)
            .concat(receivedOffers)
            .concat(receivedWithoutBids)
            .concat(submissionOffers)
            .concat(submissionWithoutBids)
            .concat(awaitingOffers)
            .concat(awaitingWithoutBids)
            .concat(otherOffers)
            .concat(otherWithoutBids);

        // Only update suppliers if this is the main data, not during initial loading
        return sortedOffers;
    }

    /**
     * Simple sort for mock offers (no status-based grouping).
     * Sorts offers by the given bid field using the current sort direction.
     */
    private applySortSimple(offers, field) {
        if (!offers || offers.length === 0) return offers;

        const sorted = [...offers];
        sorted.sort((a, b) => {
            const aVal = a.bid ? (a.bid[field] || 0) : 0;
            const bVal = b.bid ? (b.bid[field] || 0) : 0;
            return bVal - aVal;
        });
        if (this.order === 'ASC') {
            sorted.reverse();
        }
        return sorted;
    }

    public calcTotalFees(bid, unitRates = this.unitRates) {
        return FeesHelper.calcTotalFees(bid, this.currencyRates, this.baseCurrency.code, unitRates, this.baseUnit.code);
    }

    public calcTotalPerFees(bid) {
        return FeesHelper.calcTotalPerFees(bid, this.currencyRates, this.baseCurrency.code, this.unitRates);
    }

    public getEffectivePaymentTerms(bid: any): number {
        let terms = bid?.payment?.open_invoice?.payment_terms || 0;
        if (bid?.transitTime) {
            terms = Math.max(0, terms - bid.transitTime);
        }
        return terms;
    }

    /**
     * Get tooltip data for a formula cell, showing both field labels and actual values.
     */
    public getFormulaTooltip(formulaKey: string, bid: any): FormulaTooltipData | null {
        const config = this.activeFormulas[formulaKey];
        const context = bid?.['_formulaContext'];
        if (!config?.ast || !context) return null;
        return this.formulaParser.getFormulaTooltipData(config.ast, context);
    }

    /**
     * Get formula keys for dynamic formula rows (excludes total_price which has its own row).
     */
    public get dynamicFormulaKeys(): string[] {
        return Object.keys(this.activeFormulas).filter(k => k !== 'total_price');
    }

    public onUpdatedHideBids(event) {
        this.refreshContractBids();
        this.getMockOffers();
    }

    public onHideInvalidBidsChange(hideInvalidBids: boolean) {
        this.hideInvalidBids = hideInvalidBids;
        // Save preference to localStorage for this tender
        this.saveHideInvalidBidsPreference();
        this.applyInvalidBidsFilter();
    }

    /**
     * Load hideInvalidBids preference from AnalyticSetting for this tender
     */
    private loadHideInvalidBidsPreference() {
        if (this.tender && this.tender.id) {
            this.analyticsSettingsService.fetchCostOfCapital(this.tender.id).subscribe(data => {
                this.analyticSetting = data;
                this.hideInvalidBids = data.hideInvalidBids;
                this.applyInvalidBidsFilter();
            });
        }
    }

    /**
     * Save hideInvalidBids preference to AnalyticSetting for this tender
     */
    private saveHideInvalidBidsPreference() {
        if (this.tender && this.tender.id && this.analyticSetting) {
            this.analyticSetting.hideInvalidBids = this.hideInvalidBids;
            this.analyticsSettingsService.saveCostOfCapital(this.analyticSetting, this.tender.id).subscribe();
        }
    }

    private applyInvalidBidsFilter() {


        if (!this.allSuppliers.length) {
            // If allSuppliers is empty, store current suppliers
            this.allSuppliers = [...this.suppliers];
        }

        if (this.hiddenDisplayOptions > 0 || this.hideInvalidBids) {
            this.suppliers = this.allSuppliers.filter(supplier => {
                // First check manual individual hide/show (if display options are loaded)
                if (this.contractBidsDisplayOptions) {
                    const displayOption = this.contractBidsDisplayOptions.find(opt => opt.id === supplier.id);
                    if (displayOption && displayOption.display_option === false) {
                        return false;
                    }
                }

                // Then check "Hide Invalid Bids" global status
                if (this.hideInvalidBids && supplier.status && this.INVALID_BID_STATUSES.includes(supplier.status)) {
                    return false;
                }

                return true;
            });
        } else {
            // Show all suppliers
            this.suppliers = [...this.allSuppliers];
        }

        // Re-apply sorting after filtering
        this.suppliers = this.applySort(this.suppliers, this.order_field);
    }

    public updatedDisplayFieldsSetting(event) {
        let offerOrderField = event['offer_order_field']
        if (offerOrderField) {
            this.initOfferOrderField(offerOrderField);
        }

        this.usHiddenFields = event['offer_view_field'];
        this.refreshContractBids();
    }

    private initOfferOrderField(userOrderedFields) {
        userOrderedFields.forEach((name, order_index) => {
            this.usOrderedFields[name] = order_index + 10
        })
    }

    public onUpdatedBidModal(event, offer: Offer, isSubmit=false) {

        // Handle new callback structure from bid edit modal
        const offcanvas = event.offcanvas || event;
        const onComplete = event.onComplete;
        const onError = event.onError;

        if (isSubmit == true) {
            this.serviceBidSubmit.submit(this.tender, this.contract, offer.id).subscribe(items => {
                this.refreshContractBids();
                this.getMockOffers();
                offcanvas.close('Close modal');
                if (onComplete) onComplete();
            }, error => {
                if (error.error && error.error.details) {
                    // Handle both array and string formats for details
                    if (Array.isArray(error.error.details)) {
                        error.error.details.forEach((e) => {
                            if (e.message){
                                this.toast.error(e.message, e.title);
                            } else {
                                this.toast.error(e);
                            }
                        });
                    } else {
                        // Handle string format
                        this.toast.error(error.error.details);
                    }
                } else {
                    this.toast.error(error);
                }
                if (onError) onError();
            })
        } else {
            this.refreshContractBids();
            this.getMockOffers();
            offcanvas.close('Close modal');
            if (onComplete) onComplete();
        }
    }

    public onUpdatedContractModal(event, currentOffer: Offer) {
        // Show loading indicator to prevent flickering during data refresh
        this.loadingIndicatorService.show();
        this.isDataFullyLoaded = false;

        // Refresh current contracts data after contract edit
        this.getCurrentContractsAndMockBids(() => {
            // Update the display after data is refreshed
            this.updateAveragePriceBases();

            // Hide loading indicator and restore content visibility
            this.loadingIndicatorService.hide();
            this.isDataFullyLoaded = true;
        });
        event.close('Close modal');
    }

    public getMockOffers() {
        this.serviceBidMock.fetch(this.tender.id, this.contract.id).subscribe(items => {
            this.mockOffers = items;
            this.updateSecondPartOfBidsData(this.mockOffers);
            this.recalculateCombinedRanking();
            this.getAverageIndexPrices(this.mockOffers);
            // The va-table uses JS-measured row heights to keep cells
            // aligned across columns. Adding/removing a mock column
            // changes the natural height of some rows, so re-run the
            // measurement once the new column has rendered.
            this.forceLayoutRecalculation();
        }, error => {
            this.toast.error(error);
        })
    }

  public onCopyMockBid(offer, popoverMockBid) {
        if (!this.mockBidName){
            this.toast.warning('Mock Bid Name is empty');
            return
        }
        this.serviceBidMock.create(this.tender, this.contract, offer.id, {'name': this.mockBidName}).subscribe(item => {
          popoverMockBid.close();
            this.mockBidName = '';
            this.getMockOffers();
            this.toast.success('Offer has been copied');
        }, error => {
            this.toast.error(error);
        })
    }

    public removeMockBid(offer) {
        this.serviceBidMock.delete(this.tender, this.contract, offer.id).subscribe(item => {
            this.getMockOffers();
            this.toast.success('Offer has been deleted');
        }, error => {
            this.toast.error(error);
        })
    }

    public addNextOffer($event, offer: Offer) {
        this.serviceBidSubmit.createNextOffer(this.tender, this.contract, offer.id).subscribe(items => {
            this.refreshContractBids();
            this.getMockOffers();
            this.toast.success('A new offer for ' + offer.supplier.name +
                'has been created, check the table with new new offer to update it');
        }, error => {
            this.toast.error(error);
        })
    }

    public getContractStartDate(supplier: any): string {
        if (supplier?.bid?.date_contract_start) {
            // Use moment.parseZone to preserve the date without timezone conversion
            // This prevents the one-day offset issue
            return moment.parseZone(supplier.bid.date_contract_start).format('DD/MM/YYYY');
        }
        return '';
    }

    public getContractEndDate(supplier: any): string {
        if (supplier?.bid?.date_contract_end) {
            // Use moment.parseZone to preserve the date without timezone conversion
            // This prevents the one-day offset issue
            return moment.parseZone(supplier.bid.date_contract_end).format('DD/MM/YYYY');
        }
        return '';
    }

    // ─── Prev / Next location navigation ──────────────────────────────
    private recomputeContractIndex() {
        if (!this.contract?.id || !this.allContracts.length) {
            this.currentContractIndex = -1;
            return;
        }
        this.currentContractIndex = this.allContracts.findIndex(c => c.id === this.contract.id);
    }

    public get hasPreviousLocation(): boolean {
        return this.currentContractIndex > 0;
    }

    public get hasNextLocation(): boolean {
        return this.currentContractIndex >= 0
            && this.currentContractIndex < this.allContracts.length - 1;
    }

    public get previousLocationLabel(): string {
        if (!this.hasPreviousLocation) return 'Previous location';
        const c = this.allContracts[this.currentContractIndex - 1];
        const iata = (c as any)?.location?.iata;
        return iata ? `Previous: ${iata}` : 'Previous location';
    }

    public get nextLocationLabel(): string {
        if (!this.hasNextLocation) return 'Next location';
        const c = this.allContracts[this.currentContractIndex + 1];
        const iata = (c as any)?.location?.iata;
        return iata ? `Next: ${iata}` : 'Next location';
    }

    public navigatePreviousLocation() {
        if (!this.hasPreviousLocation) return;
        this.navigateToContract(this.allContracts[this.currentContractIndex - 1]);
    }

    public navigateNextLocation() {
        if (!this.hasNextLocation) return;
        this.navigateToContract(this.allContracts[this.currentContractIndex + 1]);
    }

    private navigateToContract(contract: Contract) {
        if (!contract?.id || !this.tender || !this.round) return;
        this.router.navigate([
            '/tenders', this.tender.uid,
            'locations', contract.id,
            'dashboard', 'analysis',
            'round', this.round.round,
        ]);
    }

    // ─── Fullscreen ───────────────────────────────────────────────────
    public toggleFullscreen() {
        this.isFullscreen = !this.isFullscreen;
        document.body.classList.toggle('jft-analysis-fullscreen-lock', this.isFullscreen);
        // Persist so prev/next location navigation (which destroys and
        // re-creates the component) keeps fullscreen mode active.
        if (typeof sessionStorage !== 'undefined') {
            if (this.isFullscreen) {
                sessionStorage.setItem(TenderContractBidsAnalysisComponent.FULLSCREEN_STORAGE_KEY, '1');
            } else {
                sessionStorage.removeItem(TenderContractBidsAnalysisComponent.FULLSCREEN_STORAGE_KEY);
            }
        }
        // Recalculate the analysis container height once the new layout
        // settles so existing internal scrolling logic still works.
        setTimeout(() => this.calculateVAnalysisHeight?.(), 50);
    }

    @HostListener('document:keydown.escape')
    public onEscapeFullscreen() {
        if (this.isFullscreen) {
            this.toggleFullscreen();
        }
    }
}
